import React from "react";
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import styled from "styled-components";
import { formatDistanceToNow } from "date-fns";

const SidebarContainer = styled(Box)`
    width: 240px;
    min-width: 240px;
    border-right: 1px solid ${({ theme }) => theme.palette.divider};
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
`;

const SidebarHeader = styled(Box)`
    padding: ${({ theme }) => theme.spacing(2)};
    border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
`;

const ConversationList = styled(List)`
    flex: 1;
    overflow-y: auto;
    padding: ${({ theme }) => theme.spacing(1, 0)};
`;

const ConversationItem = styled(ListItemButton)`
    padding: ${({ theme }) => theme.spacing(1, 2)};
    &:hover .delete-btn {
        opacity: 1;
    }
`;

const DeleteBtn = styled(IconButton)`
    opacity: 0;
    transition: opacity 0.15s;
    padding: 4px;
    margin-left: ${({ theme }) => theme.spacing(1)};
    &:hover {
        color: ${({ theme }) => theme.palette.error.main};
    }
`;

const ConversationSidebar = ({
    conversations,
    activeConversationId,
    loading,
    onNewConversation,
    onSelectConversation,
    onDeleteConversation,
}) => {
    return (
        <SidebarContainer>
            <SidebarHeader>
                <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={onNewConversation}
                    disabled={loading}
                >
                    New Chat
                </Button>
            </SidebarHeader>

            {loading && conversations.length === 0 ? (
                <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress size={20} />
                </Box>
            ) : (
                <ConversationList disablePadding>
                    {conversations.length === 0 && (
                        <Box p={2}>
                            <Typography variant="caption" color="text.secondary">
                                No conversations yet
                            </Typography>
                        </Box>
                    )}
                    {conversations.map((conv) => (
                        <ConversationItem
                            key={conv.id}
                            selected={conv.id === activeConversationId}
                            onClick={() => onSelectConversation(conv.id)}
                        >
                            <ListItemText
                                primary={
                                    <Typography
                                        variant="body2"
                                        noWrap
                                        sx={{
                                            maxWidth: 160,
                                            fontWeight:
                                                conv.id === activeConversationId ? 600 : 400,
                                        }}
                                    >
                                        {conv.title || "New conversation"}
                                    </Typography>
                                }
                                secondary={
                                    <Typography variant="caption" color="text.secondary">
                                        {formatDistanceToNow(new Date(conv.updatedAt), {
                                            addSuffix: true,
                                        })}
                                    </Typography>
                                }
                            />
                            <Tooltip title="Delete">
                                <DeleteBtn
                                    className="delete-btn"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDeleteConversation(conv.id);
                                    }}
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </DeleteBtn>
                            </Tooltip>
                        </ConversationItem>
                    ))}
                </ConversationList>
            )}
        </SidebarContainer>
    );
};

export default ConversationSidebar;
